import {Injectable} from '@angular/core';
import {BaseService} from './common/base.service';
import {ReservationRepository} from '../repositories/reservation.repository';
import {FilterService} from './filterer/filter.service';
import {DataManipulationService} from './common/data-manipulation.service';
import {ValidatorService} from './common/validator.service';
import {Dtos} from '../utils/dtos';
import {Constants} from '../utils/constants';
import {BillingData} from './order.service';
import {BaseModel} from '../models/base.model';
import {Utilities} from '../utils/utilities';
import {PostPayloadInterface} from '../models/post-payload.interface';
import {KeycloakService} from 'keycloak-angular';
import {MbColDefinition, MbColInput} from '../components/mb-datatable/models/mb-datatable.model';
import {MbDatatablesTemplate} from '../components/mb-datatable/mb-datatables-template';
import {CalendarType} from '../models/reservation-calendar.component';

@Injectable({
    providedIn: 'root'
})
export class ReservationService extends BaseService<ReservationRepository> {

    constructor(repo: ReservationRepository, filterService: FilterService, dataManipulationService: DataManipulationService, validatorService: ValidatorService, private keycloakService: KeycloakService) {
        super(repo, filterService, dataManipulationService, validatorService);
        this.allEdit = this.keycloakService.getUserRoles().includes(Constants.SYSTEM_ADMIN_RULE);
    }
    STATUS_TO_CALENDAR = ['DRAFT', 'PENDING', 'ACCEPTED', 'SUCCEEDED', 'PAID', 'FULFILLED'];
    private allEdit: boolean;

    private static statusOptions() {
        return [
            {label: 'enum.DRAFT', value: 'DRAFT'},
            {label: 'enum.ACCEPTED', value: 'ACCEPTED'},
            {label: 'enum.SUCCEEDED', value: 'SUCCEEDED'},
            {label: 'enum.FULFILLED', value: 'FULFILLED'},
            {label: 'enum.DECLINE_BY_RESTAURANT', value: 'DECLINE_BY_RESTAURANT'},
            {label: 'enum.DECLINE_BY_GUEST', value: 'DECLINE_BY_GUEST'},
            {label: 'enum.NOT_ARRIVED', value: 'NOT_ARRIVED'},
            {label: 'enum.PAID', value: 'PAID'},
        ];
    }

    private static reservationTypeOptions() {
        return [
            {label: 'enum.PERSONAL', value: 'PERSONAL'},
            {label: 'enum.GROUP', value: 'GROUP'},
            {label: 'enum.EXTRA', value: 'EXTRA'},
            {label: 'enum.DELIVERY', value: 'DELIVERY'}
        ];
    }

    getDefaultSpaceInMin() {
        return this.repo.getDefaultSpaceInMin();
    }

    getStatisticByRestaurantHistory(id: number, startDate: string, endDate: string) {
        return this.repo.getStatisticByRestaurantHistory(id, startDate, endDate);
    }

    reAssocBillingData(billingDataId: number, id: number, pathPostFix: string) {
        return this.repo.reAssocBillingData(billingDataId, id, pathPostFix);
    }

    changeStatus(id: number, status: string) {
        return this.repo.changeStatus(id, status);
    }

    accept(id: number) {
        return this.repo.accept(id);
    }

    pending(id) {
        return this.repo.pending(id);
    }

    success(id: number) {
        return this.repo.success(id);
    }

    notArrived(id: number) {
        return this.repo.notArrived(id);
    }

    declineReservation(reservationId: number, reason: string) {
        return this.repo.declineReservation(reservationId, reason);
    }

    declineReservationAndRegenerateVoucher(reservationId: number, reason: string) {
        return this.repo.declineReservationAndRegenerateVoucher(reservationId, reason);
    }

    getSpaceForDate(restaurantHistoryId: number, date: Date) {
        return this.repo.getSpaceForDate(restaurantHistoryId, Utilities.dateToBackendString(date));
    }

    getSpaceForAYear(restaurantHistoryId: number, reservationId?: number) {
        if (reservationId && reservationId > 0) {
            return this.getSpaceForAYearWithoutActualReservation(reservationId, restaurantHistoryId);
        }
        return this.repo.getSpaceForAYear(restaurantHistoryId);
    }

    getSpaceForAYearWithoutActualReservation(reservationId: number, restaurantHistoryId: number) {
        return this.repo.getSpaceForAYearWithoutActualReservation(reservationId, restaurantHistoryId);
    }

    getCapacityForDate(restaurantHistoryId: number, date: Date) {
        return this.repo.getCapacityForDate(restaurantHistoryId, Utilities.dateToBackendString(date));
    }

    changeDeliveryDate(id: number, newDate: string, newTime: string, duration: number) {
        return this.repo.changeDeliveryDate(id, newDate, newTime, duration);
    }

    getOneById(id: any) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardCalendarDTO)
            .equal('id', id)
            .create();
        return this.repo.getAllByFilterForStaff(filter);
    }

    getAllCalendarByRestaurantHistoryIdAndReservationDate(restaurantHistoryId: number, from: string) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardCalendarDTO)
            .equal('restaurantHistory_id', restaurantHistoryId)
            .and()
            .equal('temporary', false)
            .and()
            .greaterThanEqual('reservationDate', from)
            .and();

        this.STATUS_TO_CALENDAR.forEach((status, index) => {
            if (index < this.STATUS_TO_CALENDAR.length - 1) {
                filter.equal('reservationStatus', status).or();
            } else {
                filter.equal('reservationStatus', status);
            }
        });

        return this.repo.getAllByFilterForStaff(filter.create());
    }

    getTypeCalendarByRestaurantHistoryIdAndReservationDate(restaurantHistoryId: number, from: string, isDelivery: boolean) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardCalendarDTO)
            .equal('restaurantHistory_id', restaurantHistoryId)
            .and()
            .equal('temporary', false)
            .and();
        if (isDelivery) {
            filter.equal('type', Constants.RESERVATION_TYPES.DELIVERY).and();
        } else {
            filter.notEqual('type', Constants.RESERVATION_TYPES.DELIVERY).and();
        }
        filter.greaterThanEqual('reservationDate', from)
            .and();

        this.STATUS_TO_CALENDAR.forEach((status, index) => {
            if (index < this.STATUS_TO_CALENDAR.length - 1) {
                filter.equal('reservationStatus', status).or();
            } else {
                filter.equal('reservationStatus', status);
            }
        });

        return this.repo.getAllByFilterForStaff(filter.create());
    }

    getTypeCalendarByRestaurantHistoryIdAndReservationDateTest(restaurantHistoryId: number, from: string, isDelivery = false) {
        return this.repo.getAllByFilterForStaffTest(restaurantHistoryId, from);
    }

    getAllListViewByRestaurantHistoryIdAndReservationDate(restaurantHistoryId: number, from: Date, to: Date) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardCalendarDTO)
            .equal('restaurantHistory_id', restaurantHistoryId)
            .and()
            .greaterThanEqual('reservationDate', Utilities.dateToBackendString(from))
            .and()
            .lessThanEqual('reservationDate', Utilities.dateToBackendString(to))
            .create();
        return this.repo.getAllByFilterForStaff(filter);
    }

    getTypeListViewByRestaurantHistoryIdAndReservationDate(restaurantHistoryId: number, from: Date, to: Date, isDelivery: boolean) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardCalendarDTO)
            .equal('restaurantHistory_id', restaurantHistoryId)
            .and()
            .equal('temporary', false)
            .and();
        if (isDelivery) {
            filter.equal('type', Constants.RESERVATION_TYPES.DELIVERY).and();
        } else {
            filter.notEqual('type', Constants.RESERVATION_TYPES.DELIVERY).and();
        }
        filter.greaterThanEqual('reservationDate', Utilities.dateToBackendString(from))
            .and()
            .lessThanEqual('reservationDate', Utilities.dateToBackendString(to))
        return this.repo.getAllByFilterForStaff(filter.create());
    }


    getAllDraftByRestaurantHistoryIdAndReservationDate(restaurantHistoryIds: number[], from: Date) {
        const filter = this.filterService.filter(Dtos.RESERVATION.ReservationForDashboardNotificationDTO)
            .equal('reservationStatus', 'DRAFT')
            .and()
            .greaterThanEqual('reservationDate', Utilities.dateToBackendString(from))
            .and()
            .equal('temporary', false)
            .and()
            .in('restaurantHistory_id', restaurantHistoryIds);
        // restaurantHistoryIds.forEach((historyId, index) => {
        //     if (index < restaurantHistoryIds.length - 1) {
        //         filter.equal('restaurantHistory_id', historyId).or();
        //     } else {
        //         filter.equal('restaurantHistory_id', historyId);
        //     }
        // });
        return this.repo.getAllByFilterForStaff(filter.create());
    }

    createEventTime(reservation: any, start: boolean) {
        let date = new Date(reservation.reservationDate + ' ' + reservation.reservationTime);
        if (!start) {
            date = Utilities.addMinutesToDate(date, (reservation.durationInMinutes + reservation.laufInMinutes));
        }
        return date;
    }

    getReservationTitle(reservation: any, type?: string): string {
        let name = 'general.unknown';
        let productNames: any = [];
        if (reservation.products != null) {
            reservation.products.forEach(product => {
                if (product.localizations && product.localizations[this.dataManipulationService.translate.currentLang]) {
                    productNames.push(product.localizations[this.dataManipulationService.translate.currentLang].name);
                }
            });
            productNames = productNames.join(',');
        }

        if (reservation.billingData != null) {
            name = reservation.billingData.firstName + ' ' + reservation.billingData.lastName;
        }


        const personCountTitle = this.dataManipulationService.translate.instant('reservation.personCountTitle');
        const typeTitle = this.dataManipulationService.translate.instant('enum.' + type);
        const survey = reservation.survey && reservation.survey.length > 0 ? this.dataManipulationService.translate.instant(reservation.survey[0]) : '';
        return `${this.allEdit ? reservation.id : ''} ${reservation.exclusive ? 'EXCLUSIVE' : ''} ${type ? typeTitle : ''} ${name} ${this.hasNote(reservation) ? 'ICON' : ''}  ${reservation.tookVoucher? 'BOOKMARK' : ''}
                            ${reservation.personCount} ${personCountTitle}
                            ${productNames}
                            ${survey}
                            `;
    }

    private hasNote(reservation: any) {
        let hasNote = reservation.notes && reservation.notes.length > 0;
        if (!hasNote && reservation.voucher) {
            hasNote = reservation.voucher.orderMessage.length > 0;
        }
        return hasNote;
    }

    generateClassForEvent(reservation) {
        if (this.reservationIsDeclined(reservation)) {
            return 'bg-dark text-white';

        }
        if (reservation.deliveryReservation == null) {
            return Constants.RESERVATION_STATUS_COLOR_MAP[reservation.reservationStatus] + ' text-black';
        }

        return Constants.DELIVERY_TRACK_COLOR_MAP[reservation.deliveryReservation.deliveryStatus] + ' text-black';
    }

    reservationIsDeclined(reservation): boolean {
        return reservation.reservationStatus == 'DECLINE_BY_RESTAURANT'
            || reservation.reservationStatus == 'DECLINE_BY_GUEST'
    }

    reservationEditable(reservation) {
        if (reservation.type == Constants.RESERVATION_TYPES.DELIVERY) {
            return !this.reservationIsDeclined(reservation) && reservation.reservationStatus != 'SUCCEEDED'
        } else {
            return !this.reservationIsDeclined(reservation) &&
                reservation.reservationStatus != 'SUCCEEDED' && reservation.reservationStatus != 'NOT_ARRIVED'
        }
    }


    validate(model: ReservationModel, checkMaxDuration?: boolean) {
        if (model.personCount < 1) {
            throw new Error('error.reservation.invalidPersonCount');
        }

        if (checkMaxDuration && model.durationInMinutes < 120) {
            throw new Error('error.reservation.invalidDuration');
        }

        if (model.billingData.email.length < 1) {
            throw new Error('error.reservation.personalInformation');
        }

        if (model.type != 'EXTRA' && model.type != 'DELIVERY') {
            if (model.personCount > 8 && model.type != 'GROUP') {
                throw new Error('error.reservation.wrongType');
            }
        }
    }

    validateBeforeUpdate(model: ReservationModel) {
        this.validate(model);
        if (model.reservationStatus != 'DRAFT' && model.reservationStatus != 'ACCEPTED' && model.reservationStatus != 'PENDING') {
            throw new Error('error.reservation.canNotUpdateThisReservation')
        }
    }

    finalize(reservationNumber: string, skipSpotValidation: boolean) {
        return this.repo.finalize(reservationNumber, skipSpotValidation);
    }

    upgradeToExtraByVoucher(reservationNumber, productNumber) {
        const payload: PostPayloadInterface = {
            id: null,
            type: '',
            attribute: {
                reservationNumber,
                productNumber
            }
        };
        return this.repo.upgradeToExtraByVoucher(payload);
    }

    upgradeToExtraByProduct(reservationNumber: string, productIds: number[], withDelete?: boolean) {
        const payload: PostPayloadInterface = {
            id: null,
            type: '',
            attribute: {
                reservationNumber,
                productIds
            }
        };
        if (withDelete) {
            return this.repo.upgradeToExtraByProductWithDelete(payload);
        }
        return this.repo.upgradeToExtraByProduct(payload);
    }

    downgradeExtra(reservationNumber: string, withoutDelete?: boolean) {
        const payload: PostPayloadInterface = {
            id: null,
            type: '',
            attribute: {
                reservationNumber
            }
        };
        if(withoutDelete){
            return this.repo.downgradeExtraWithoutDelete(payload);
        }
        return this.repo.downgradeExtra(payload);
    }

    upgradeToDeliveryByVoucher(reservationNumber, productNumber, deliveryData) {
        const payload: PostPayloadInterface = {
            id: null,
            type: '',
            attribute: {
                reservationNumber,
                productNumber,
                deliveryData
            }
        };
        return this.repo.upgradeToDeliveryByVoucher(payload);
    }

    updateBaseDraft(reservation) {
        const payload: PostPayloadInterface = {
            id: reservation.id,
            type: 'draft',
            attribute: reservation
        };
        return this.repo.updateBase(payload);
    }

    updateBaseAccepted(reservation) {
        const payload: PostPayloadInterface = {
            id: reservation.id,
            type: 'accepted',
            attribute: reservation
        };
        return this.repo.updateBase(payload);
    }

    updateBasePending(reservation) {
        const payload: PostPayloadInterface = {
            id: reservation.id,
            type: 'pending',
            attribute: reservation
        };
        return this.repo.updateBase(payload);
    }

    getFoodEditLink(reservation: any, isDelivery: boolean) {
        if (reservation.reservationDate == Utilities.dateToBackendString(new Date()) && !isDelivery) {
            return Utilities.getRestaurantAppRedirectUrl() + '#reservation-' + reservation.id;
        }
        return Utilities.getFoodSelectorRedirectUrl() + 'home/b' + reservation.reservationNumber + 'o/s' + reservation.reservationPassword + 's/summary';
    }

    filterReservations(reservations: any[], startDate: Date, endDate: Date) {
        const startDatePart = Utilities.dateToBackendString(startDate);
        const endDatePart = Utilities.dateToBackendString(endDate);
        return reservations.filter(reservation => {
            return startDatePart <= reservation.reservationDate && reservation.reservationDate <= endDatePart
        })
    }

    decrementRestaurantReservationPersonCount(reservation: any, guestIdsToBeDeleted: number[]) {
        const payload: PostPayloadInterface = {
            id: reservation.id,
            type: 'decrement-person-count',
            attribute: {
                reservationNumber: reservation.reservationNumber,
                newType: reservation.type,
                newPersonCount: reservation.personCount,
                guestIdsToBeDeleted
            }
        };
        return this.repo.decrementRestaurantReservationPersonCount(payload);
    }
    incrementRestaurantReservationPersonCount(reservation: any, guests: any[]) {
        const payload: PostPayloadInterface = {
            id: reservation.id,
            type: 'increment-person-count',
            attribute: {
                reservationNumber: reservation.reservationNumber,
                newType: reservation.type,
                newPersonCount: reservation.personCount,
                guests
            }
        };
        return this.repo.incrementRestaurantReservationPersonCount(payload);
    }

    public addExtraDataForOne(reservation: any) {
        this.dataManipulationService.addNameAttributeFromFirstAndLastName(reservation.billingData);
        reservation.reservationDateTime = reservation.reservationDate + 'T' + reservation.reservationTime;

        if (reservation.type == Constants.RESERVATION_TYPES.DELIVERY) {
            let endTime = new Date();
            if (reservation.reservationTime) {
                endTime.setHours(reservation.reservationTime.slice(0, 2));
                endTime.setMinutes(reservation.reservationTime.slice(3, 2));
                endTime = endTime.addMinutes(reservation.durationInMinutes);
                reservation.timeInterval = reservation.reservationTime.slice(0, 5) + ' - ' + Utilities.onlyTimeToBackendString(endTime).slice(0, 5);
            } else {
                reservation.reservationTime = '06:00';
            }
        }
        reservation.isEvent = reservation.makeryEvent != undefined;

        return reservation;
    }

    filterReservationsByName(reservations: any[], fullName: string) {
        return reservations.filter((reservation: any) => {
            const firstNameLastName = reservation.billingData.firstName + ' ' + reservation.billingData.lastName;
            const lastNameFirstName = reservation.billingData.lastName + ' ' + reservation.billingData.firstName;
            return firstNameLastName.toLowerCase().includes(fullName.toLowerCase()) ||
                lastNameFirstName.toLowerCase().includes(fullName.toLowerCase());
        }).sort((firstElement, secondElement) => {
            if (firstElement.reservationDate == secondElement.reservationDate) {
                return firstElement.reservationTime < secondElement.reservationTime ? 1 : -1;
            }
            return firstElement.reservationDate < secondElement.reservationDate ? 1 : -1;
        });
    }

    defineModelSpecificColumns(extraInfo?: any) {
        const retList = [];

        if (extraInfo == 'all') {
            const typeOptions = [];
            Object.values(Constants.RESERVATION_TYPES).forEach(type => {
                typeOptions.push({label: 'enum.' + type, value: type});
            });
            retList.push(
                MbColDefinition.new('type', 'reservation.table.type', MbColInput.new(MbColInput.SELECT, typeOptions), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
                MbColDefinition.new('reservationDateTime', 'reservation.table.reservationDateTime', MbColInput.newDefaultText(), MbDatatablesTemplate.DATETIME_TEMPLATE)
            );
        } else if (extraInfo == Constants.RESERVATION_TYPES.DELIVERY) {
            retList.push(
                MbColDefinition.new('reservationDate', 'reservation.table.deliveryDate'),
                MbColDefinition.new('timeInterval', 'reservation.table.timeInterval')
            );
        } else {
            retList.push(
                MbColDefinition.new('reservationDateTime', 'reservation.table.reservationDateTime', MbColInput.newDefaultText(), MbDatatablesTemplate.DATETIME_TEMPLATE),
                MbColDefinition.new('durationInMinutes', 'reservation.table.duration')
            );
        }

        retList.push(
            MbColDefinition.new('reservationStatus', 'reservation.table.reservationStatus',
                MbColInput.new(MbColInput.SELECT, ReservationService.statusOptions()), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('type', 'reservation.table.reservationType',
                MbColInput.new(MbColInput.SELECT, ReservationService.reservationTypeOptions()), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
            MbColDefinition.new('billingData.name', 'reservation.table.customerName'),
            MbColDefinition.new('billingData.email', 'reservation.table.customerEmail'),
            MbColDefinition.new('personCount', 'reservation.table.personCount'),
            MbColDefinition.new('isEvent', 'reservation.isEvent', MbColInput.new(MbColInput.SELECT, [
                {label: 'general.true', value: 'true'},
                {label: 'general.false', value: 'false'}
            ]), MbDatatablesTemplate.TRANSLATE_TEMPLATE),
        );

        return retList;
    }

    getAllForCalendar(calendarType: CalendarType, restaurantHistoryId: number, startDate: string) {
        return this.getTypeCalendarByRestaurantHistoryIdAndReservationDate(restaurantHistoryId, startDate, calendarType.name == 'DELIVERY');
    }
    getAllForCalendarTest(calendarType: CalendarType, restaurantHistoryId: number, startDate: string) {
        return this.getTypeCalendarByRestaurantHistoryIdAndReservationDateTest(restaurantHistoryId, startDate);
    }

    toggleExclusive(id: number) {
        return this.repo.toggleExclusive(id);
    }

    reSendEmail(id: number) {
        return this.repo.reSendEmail(id);
    }

    updateReservationLanguage(id, reservationLanguage) {
        return this.repo.updateReservationLanguage(id, reservationLanguage);
    }

    getSummarize(reservationType: string, restaurantHistoryId: number, from: Date, to: Date) {
        return this.repo.getSummarize(reservationType, restaurantHistoryId, from, to);
    }

    tookVoucher(id:number) {
        return this.repo.tookVoucher(id);
    }
}

export class ReservationModel implements BaseModel {
    id: number = null;
    type = 'PERSONAL';
    restaurantHistoryId: any;
    reservationDate: string;
    reservationTime: string;
    billingData: BillingData = new BillingData();
    laufInMinutes = 0;
    durationInMinutes = 120;
    personCount = 0;
    exclusive = false;
    reservationUser: any;
    reservationStatus = 'DRAFT';

    constructor(type?: string, restaurantHistoryId?: any, reservationDateTime?: Date) {
        this.type = type ? type : Constants.RESERVATION_TYPES.PERSONAL;
        this.restaurantHistoryId = restaurantHistoryId ? restaurantHistoryId : null;

        if (reservationDateTime) {
            this.reservationDate = Utilities.dateToBackendString(reservationDateTime);
            this.reservationTime = Utilities.onlyTimeToBackendString(reservationDateTime);
        }

        this.restaurantHistoryId = restaurantHistoryId ? restaurantHistoryId : null;
        this.laufInMinutes = 0;
    }

}
